/**
 * The shipped audience's stored size, as a caption under the activation header's title row.
 *
 * A THIRD band rather than anything inside the `<h1>`. The strip is a relationship (audience →
 * destination) and size belongs to its near side only, so putting it mid-strip would read as a
 * property of the pairing. It also has to stay out of the heading's accessible name: that name is
 * deliberately minimal, and a periodically-refetched number inside it would make a landmark's name
 * change as the number moves.
 *
 * Bare `CountBadge` + `StaleIndicator`, never `AudienceSizeBar`: this page can recount nothing — it owns
 * neither the definition nor the count — so it must not offer the refresh affordance the audience page
 * pairs with staleness. That is the same pairing the audience list and chat card already use, and
 * `CountBadge`'s own doc names them as the bare-context precedent.
 */
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { CountBadge } from '@/audiences/components/CountBadge';
import { StaleIndicator } from '@/audiences/components/StaleIndicator';
import { type AudienceRecord, countStateOf, isStale } from '@/audiences/lib/audience-view';

export function AudienceSizeCaption({
  record,
  loading,
  error,
}: {
  record: AudienceRecord | null;
  loading: boolean;
  error?: Error | null;
}): React.ReactElement | null {
  /**
   * ABSENT and UNKNOWN are different facts here, and only one of them is worth a placeholder.
   *
   * A sync Lookout did not mint has no audience row and never will (`useSyncDetail`'s
   * `lookoutAudienceId` doc), so there is nothing to wait for and nothing to say — it renders as
   * NOTHING, not as "Unknown", which would claim a fact exists and is merely uncounted. `loading` is
   * true only once a row this sync actually has is in flight, which is why the skeleton cannot appear
   * for the absent case and cannot hold a line open for a fact that is never coming.
   */
  if (record === null) {
    if (loading) return <Skeleton className="mt-1 h-4 w-40" />;
    /**
     * THREE outcomes reach `record === null`, and collapsing them was the bug this branch exists to
     * prevent. Absent (no Lookout audience, and never will be) renders nothing. Loading renders a
     * skeleton. FAILED renders a line saying so — because a blank caption on a sync that does have an
     * audience claims the size does not exist, when the truth is we could not read it. The page's
     * Retry refetches this query, so the words are actionable rather than decorative.
     */
    if (error) {
      return (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="sr-only">Audience size: </span>
          <span>Couldn&apos;t load audience size</span>
        </p>
      );
    }
    return null;
  }

  return (
    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      {/* The audience page states "Size" beside this; here the chip stands alone, so the name it
          would otherwise borrow from context has to be said out loud for a screen reader. */}
      <span className="sr-only">Audience size: </span>
      <CountBadge state={countStateOf(record)} />
      {isStale(record) ? <StaleIndicator /> : null}
    </p>
  );
}
